import { useState, useEffect, useCallback } from 'react';
import { useAuth } from './useAuth';
import { useImpersonation } from './useImpersonation';
import { supabase } from '@/integrations/supabase/client';

interface PartnerData {
  id: string;
  partner_name: string;
  subdomain_prefix: string;
  revenue_share_percentage: number;
  logo_url: string | null;
  primary_color: string | null;
  welcome_message: string | null;
  contact_name: string | null;
  contact_email: string | null;
  phone: string | null;
  website: string | null;
  w9_completed: boolean | null;
  w9_file_url: string | null;
  payout_account_connected: boolean | null;
  memberRole?: string; // Role when user is a team member (not owner)
}

const ACTIVE_PARTNER_KEY = 'activePartnerId';

export const usePartnerRole = () => {
  const { user, loading: authLoading } = useAuth();
  const { effectiveUserId, isImpersonating } = useImpersonation();
  const [isPartner, setIsPartner] = useState(false);
  const [partners, setPartners] = useState<PartnerData[]>([]);
  const [activePartnerId, setActivePartnerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  const fetchPartnerRole = useCallback(async () => {
    // Don't fetch if auth is still loading
    if (authLoading) {
      return;
    }
    
    // Use effectiveUserId when impersonating, otherwise use actual user id
    const targetUserId = effectiveUserId || user?.id;
    
    if (!targetUserId) {
      setIsPartner(false);
      setPartners([]);
      setActivePartnerId(null);
      setLoading(false);
      setHasFetched(true);
      return;
    }

    // Set loading true before fetching
    setLoading(true);

    try {
      console.log('usePartnerRole: Fetching partner data for user:', targetUserId, isImpersonating ? '(impersonating)' : '');
      
      // Check if user has partner role (skip if impersonating - we trust the admin selected a partner)
      if (!isImpersonating) {
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', targetUserId)
          .eq('role', 'partner')
          .maybeSingle();

        console.log('usePartnerRole: Role query result:', { roleData, roleError });

        if (!roleData) {
          // User doesn't have partner role, but check if they're a team member
          // Team members might not have the partner role assigned
        }
      }

      // Fetch partners where user is the primary owner
      const { data: ownedPartners, error: ownedError } = await supabase
        .from('partner_subdomains')
        .select('id, partner_name, subdomain_prefix, revenue_share_percentage, logo_url, primary_color, welcome_message, contact_name, contact_email, phone, website, w9_completed, w9_file_url, payout_account_connected')
        .eq('partner_user_id', targetUserId)
        .eq('status', 'active');

      console.log('usePartnerRole: Owned partners:', { ownedPartners, ownedError });

      // Fetch partners where user is a team member
      const { data: membershipData, error: memberError } = await supabase
        .from('partner_team_members')
        .select(`
          role,
          partner_subdomain_id,
          partner_subdomains (
            id, partner_name, subdomain_prefix, revenue_share_percentage, 
            logo_url, primary_color, welcome_message, contact_name, 
            contact_email, phone, website, w9_completed, w9_file_url, 
            payout_account_connected, status
          )
        `)
        .eq('user_id', targetUserId)
        .eq('status', 'active');

      console.log('usePartnerRole: Team memberships:', { membershipData, memberError });

      // Combine and deduplicate partners
      const allPartners: PartnerData[] = [];
      const seenIds = new Set<string>();

      // Add owned partners first (they take priority)
      if (ownedPartners) {
        for (const partner of ownedPartners) {
          if (!seenIds.has(partner.id)) {
            seenIds.add(partner.id);
            allPartners.push({
              ...partner,
              memberRole: 'owner',
            });
          }
        }
      }

      // Add team member partners
      if (membershipData) {
        for (const membership of membershipData) {
          const partner = membership.partner_subdomains as unknown as (PartnerData & { status: string });
          if (partner && partner.status === 'active' && !seenIds.has(partner.id)) {
            seenIds.add(partner.id);
            allPartners.push({
              id: partner.id,
              partner_name: partner.partner_name,
              subdomain_prefix: partner.subdomain_prefix,
              revenue_share_percentage: partner.revenue_share_percentage,
              logo_url: partner.logo_url,
              primary_color: partner.primary_color,
              welcome_message: partner.welcome_message,
              contact_name: partner.contact_name,
              contact_email: partner.contact_email,
              phone: partner.phone,
              website: partner.website,
              w9_completed: partner.w9_completed,
              w9_file_url: partner.w9_file_url,
              payout_account_connected: partner.payout_account_connected,
              memberRole: membership.role,
            });
          }
        }
      }

      console.log('usePartnerRole: All accessible partners:', allPartners);

      if (allPartners.length > 0) {
        setIsPartner(true);
        setPartners(allPartners);

        // Restore active partner from localStorage or use first one
        const storedActiveId = localStorage.getItem(ACTIVE_PARTNER_KEY);
        const validActiveId = storedActiveId && allPartners.some(p => p.id === storedActiveId)
          ? storedActiveId
          : allPartners[0].id;
        
        setActivePartnerId(validActiveId);
        localStorage.setItem(ACTIVE_PARTNER_KEY, validActiveId);
      } else {
        setIsPartner(false);
        setPartners([]);
        setActivePartnerId(null);
        localStorage.removeItem(ACTIVE_PARTNER_KEY);
      }
    } catch (error) {
      console.error('usePartnerRole: Error fetching partner role:', error);
      setIsPartner(false);
      setPartners([]);
      setActivePartnerId(null);
    }

    setLoading(false);
    setHasFetched(true);
  }, [effectiveUserId, user?.id, authLoading, isImpersonating]);

  useEffect(() => {
    // Reset hasFetched when user or impersonation changes
    if (!user && !isImpersonating) {
      setHasFetched(false);
    }
    fetchPartnerRole();
  }, [fetchPartnerRole, user, effectiveUserId]);

  const switchPartner = useCallback((partnerId: string) => {
    const partner = partners.find(p => p.id === partnerId);
    if (partner) {
      setActivePartnerId(partnerId);
      localStorage.setItem(ACTIVE_PARTNER_KEY, partnerId);
    }
  }, [partners]);

  const refetch = useCallback(() => {
    fetchPartnerRole();
  }, [fetchPartnerRole]);

  // Consider loading if auth is loading OR we haven't fetched yet for this user
  const effectiveLoading = authLoading || (user && !hasFetched) || loading;

  // Get active partner data for backward compatibility
  const activePartner = partners.find(p => p.id === activePartnerId) || null;

  return { 
    isPartner, 
    partners,
    activePartner,
    partnerId: activePartnerId, 
    partnerData: activePartner, // Backward compatibility
    loading: effectiveLoading, 
    switchPartner,
    refetch 
  };
};
